import { useState, useRef, useCallback, useEffect, useMemo } from "react"
import { nanoid } from "nanoid"
import VectorLayer, {
    pointsToSvgPath,
    getElementBounds,
    getElementLocalBounds,
    getMultiElementBounds,
    getHandlePositions,
    getRotationHandlePos,
    getBoundsCenter,
    getCropRect,
    getCropHandlePositions,
    hasCrop,
    HANDLE_SIZE,
    CROP_HANDLE_SIZE,
    ROTATION_HANDLE_RADIUS,
} from "./VectorLayer"
import LayerPanel from "./LayerPanel"
import {
    DEFAULT_LAYER_FILTERS,
    appendSvgFilterPrimitives,
    normalizeLayerFilters,
    blendModeToCanvas,
} from "./layerFilters"
import Toolbar from "./Toolbar"
import Button from "@/components/ui/Button"
import { FiZoomIn, FiZoomOut, FiEdit2 } from "react-icons/fi"
import { FaChevronLeft } from "react-icons/fa"
import { apiFetch } from '@/lib/api'
import { getFileUrl } from '@/lib/s3'
import { useShow } from "@/contexts/ShowContext"
import useKagamiBridge from "@/hooks/useKagamiBridge"
import { useModal } from "@/contexts/ModalContext"
import AssetsBrowserModal from "@/components/modals/AssetsBrowserModal"
import StoryboardGenerations, { GENERATION_DRAG_TYPE } from "@/components/storyboard/StoryboardGenerations"
import { DragPreviewProvider } from "@/components/ui/DragPreview"
import Loader from "@/components/loader"

function createLayer(name, order = 0) {
    return {
        _id: nanoid(),
        name: name || 'Layer',
        visible: true,
        locked: false,
        opacity: 1,
        order,
        elements: [],
        filters: { ...DEFAULT_LAYER_FILTERS },
    }
}

export default function SketchEditor({ sketchId, onClose, onSaveComplete, initialImageUrl, initialName, onionImages = [], width = 1920, height = 1080 }) {
    const { show } = useShow()
    const { runTaskAndPoll, taskStatus: bgTaskStatus } = useKagamiBridge()
    const [removingBg, setRemovingBg] = useState(false)
    const [composing, setComposing] = useState(false)
    const [layers, setLayers] = useState([createLayer('Layer 1', 0)])
    const [activeLayerId, setActiveLayerId] = useState(null)
    const [brushColor, setBrushColor] = useState('#000000')
    const [brushSize, setBrushSize] = useState(4)
    const [mode, setMode] = useState('transform')
    const [bgColor, setBgColor] = useState('#ffffff')
    const [sketchName, setSketchName] = useState(initialName || 'Untitled Edit')
    const [isRenaming, setIsRenaming] = useState(false)
    const renameInputRef = useRef(null)
    useEffect(() => {
        if (isRenaming && renameInputRef.current) {
            renameInputRef.current.focus()
            renameInputRef.current.select()
        }
    }, [isRenaming])
    const [saving, setSaving] = useState(false)
    const [fillVertical, setFillVertical] = useState(false)
    const [cursorVisible, setCursorVisible] = useState(false)
    const [dbSketchId, setDbSketchId] = useState(sketchId || null)
    const [loading, setLoading] = useState(!!sketchId)
    const [initialImageLoaded, setInitialImageLoaded] = useState(false)

    const clampedOnionImages = useMemo(() => onionImages.slice(0, 10), [onionImages])
    const [onionEnabled, setOnionEnabled] = useState(false)
    const [onionCount, setOnionCount] = useState(clampedOnionImages.length)
    const [onionOpacity, setOnionOpacity] = useState(0.6)

    useEffect(() => {
        setOnionCount(prev => Math.min(prev, clampedOnionImages.length) || clampedOnionImages.length)
    }, [clampedOnionImages.length])

    const [activeShape, setActiveShape] = useState(null)
    const [transformAction, setTransformAction] = useState(null)
    const [selectedElementIds, setSelectedElementIds] = useState([])
    const [groupRotation, setGroupRotation] = useState(0)
    const [frozenBounds, setFrozenBounds] = useState(null)
    const [frozenCenter, setFrozenCenter] = useState(null)
    const [marquee, setMarquee] = useState(null)
    const { openModal } = useModal()
    const [generationsOpen, setGenerationsOpen] = useState(false)
    const [cropTargetId, setCropTargetId] = useState(null)
    const [cropEditRect, setCropEditRect] = useState(null)
    const [snapLines, setSnapLines] = useState(null)
    const lastClickRef = useRef({ time: 0, x: 0, y: 0 })
    const applyCropRef = useRef(null)

    const SNAP_THRESHOLD = 6

    const [zoom, setZoom] = useState(1)
    const [panX, setPanX] = useState(0)
    const [panY, setPanY] = useState(0)
    const [isPanning, setIsPanning] = useState(false)
    const panStartRef = useRef(null)
    const spaceHeldRef = useRef(false)

    const cursorRef = useRef(null)
    const canvasWrapperRef = useRef(null)
    const sizingRef = useRef(null)
    const viewportRef = useRef(null)
    const clipboard = useRef([])
    const undoStack = useRef([])
    const redoStack = useRef([])
    const [historyVersion, setHistoryVersion] = useState(0)
    const MAX_UNDO = 80

    const snapshotState = useCallback(() => {
        return JSON.parse(JSON.stringify({ layers, activeLayerId, bgColor }))
    }, [layers, activeLayerId, bgColor])

    const pushUndo = useCallback(() => {
        undoStack.current.push(snapshotState())
        if (undoStack.current.length > MAX_UNDO) undoStack.current.shift()
        redoStack.current = []
        setHistoryVersion(v => v + 1)
    }, [snapshotState])

    const undo = useCallback(() => {
        if (undoStack.current.length === 0) return
        redoStack.current.push(snapshotState())
        const prev = undoStack.current.pop()
        setLayers(prev.layers)
        setActiveLayerId(prev.activeLayerId)
        setBgColor(prev.bgColor)
        setSelectedElementIds([])
        setGroupRotation(0)
        setFrozenBounds(null)
        setFrozenCenter(null)
        setHistoryVersion(v => v + 1)
    }, [snapshotState])

    const redo = useCallback(() => {
        if (redoStack.current.length === 0) return
        undoStack.current.push(snapshotState())
        const next = redoStack.current.pop()
        setLayers(next.layers)
        setActiveLayerId(next.activeLayerId)
        setBgColor(next.bgColor)
        setSelectedElementIds([])
        setGroupRotation(0)
        setFrozenBounds(null)
        setFrozenCenter(null)
        setHistoryVersion(v => v + 1)
    }, [snapshotState])

    const canUndo = undoStack.current.length > 0
    const canRedo = redoStack.current.length > 0

    useEffect(() => {
        if (layers.length > 0 && !activeLayerId) {
            setActiveLayerId(layers[0]._id)
        }
    }, [layers, activeLayerId])

    useEffect(() => {
        if (initialImageLoaded || sketchId) return
        if (!initialImageUrl) return
        const bgLayer = createLayer('Background', 0)
        bgLayer.elements = [{
            _id: nanoid(),
            kind: 'image',
            imageUrl: initialImageUrl,
            x: 0,
            y: 0,
            scaleX: 1,
            scaleY: 1,
            rotation: 0,
        }]
        setLayers([bgLayer])
        setActiveLayerId(bgLayer._id)
        setInitialImageLoaded(true)
    }, [initialImageUrl, sketchId, initialImageLoaded])

    useEffect(() => {
        if (!sketchId || !show) return
        setLoading(true)
        apiFetch(`/api/shows/${show._id}/sketches/${sketchId}`)
            .then(r => r.json())
            .then(data => {
                if (data.error) return
                setSketchName(data.name)
                setBgColor(data.bgColor || '#ffffff')
                if (data.layers?.length) {
                    const loaded = data.layers.map(l => ({
                        _id: l._id || nanoid(),
                        name: l.name || 'Layer',
                        visible: l.visible ?? true,
                        locked: l.locked ?? false,
                        opacity: l.opacity ?? 1,
                        order: l.order ?? 0,
                        filters: {
                            ...DEFAULT_LAYER_FILTERS,
                            ...(l.filters || {}),
                        },
                        elements: (l.elements || []).map(el => ({
                            ...el,
                            _id: el._id || nanoid(),
                        })),
                    }))
                    setLayers(loaded)
                    setActiveLayerId(loaded[0]._id)
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [sketchId, show])

    const activeLayer = layers.find(l => l._id === activeLayerId)
    const sortedLayers = [...layers].sort((a, b) => a.order - b.order)

    useEffect(() => {
        setSelectedElementIds([])
        setGroupRotation(0)
        setFrozenBounds(null)
        setFrozenCenter(null)
        if (cropTargetId) applyCropRef.current?.()
    }, [mode])

    const selectableLayers = useMemo(() => {
        return sortedLayers.filter(l => l.visible && !l.locked)
    }, [sortedLayers])

    const selectedElements = useMemo(() => {
        if (selectedElementIds.length === 0) return []
        const idSet = new Set(selectedElementIds)
        const result = []
        for (const layer of selectableLayers) {
            for (const el of (layer.elements || [])) {
                if (idSet.has(el._id)) result.push(el)
            }
        }
        return result
    }, [selectableLayers, selectedElementIds])

    const singleRotatedImage = selectedElements.length === 1
        && selectedElements[0].kind === 'image'
        && (selectedElements[0].rotation ?? 0) !== 0
        ? selectedElements[0]
        : null

    const liveBounds = useMemo(() => {
        if (selectedElements.length === 0) return null
        if (singleRotatedImage) {
            if (hasCrop(singleRotatedImage)) {
                return getCropRect(singleRotatedImage, width, height)
            }
            return getElementLocalBounds(singleRotatedImage, width, height)
        }
        if (selectedElements.length === 1 && selectedElements[0].kind === 'image' && hasCrop(selectedElements[0])) {
            return getCropRect(selectedElements[0], width, height)
        }
        return getMultiElementBounds(selectedElements, width, height)
    }, [selectedElements, width, height, singleRotatedImage])

    const liveRotation = frozenBounds
        ? groupRotation
        : singleRotatedImage
            ? (singleRotatedImage.rotation ?? 0)
            : groupRotation

    const selectionBounds = frozenBounds ?? liveBounds
    const selectionCenter = frozenCenter ?? (selectionBounds ? getBoundsCenter(selectionBounds) : null)

    const selectionPivot = useMemo(() => {
        if (selectedElements.length === 1 && selectedElements[0].kind === 'image') {
            const el = selectedElements[0]
            if (hasCrop(el) && (el.rotation ?? 0) !== 0) {
                const full = getElementLocalBounds(el, width, height)
                return full ? getBoundsCenter(full) : selectionCenter
            }
        }
        return selectionCenter
    }, [selectedElements, width, height, selectionCenter])

    // --- Zoom / Pan ---

    const MIN_ZOOM = 0.5
    const MAX_ZOOM = 2

    const resetZoom = useCallback(() => {
        setZoom(1)
        setPanX(0)
        setPanY(0)
    }, [])

    const zoomToCenter = useCallback((factor) => {
        const el = sizingRef.current
        if (!el) return
        const cx = el.offsetWidth / 2
        const cy = el.offsetHeight / 2
        setZoom(prev => {
            const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev * factor))
            const ratio = next / prev
            setPanX(px => cx - ratio * (cx - px))
            setPanY(py => cy - ratio * (cy - py))
            return next
        })
    }, [])

    // --- Keyboard shortcuts ---

    const BRUSH_SIZES = [2, 4, 8, 16, 32]

    const deleteSelectedElements = useCallback(() => {
        if (selectedElementIds.length === 0) return
        pushUndo()
        const idSet = new Set(selectedElementIds)
        setLayers(prev => prev.map(l => {
            const hasAny = (l.elements || []).some(el => idSet.has(el._id))
            if (!hasAny) return l
            return { ...l, elements: (l.elements || []).filter(el => !idSet.has(el._id)) }
        }))
        setSelectedElementIds([])
        setGroupRotation(0)
    }, [selectedElementIds, pushUndo])

    const copyElements = useCallback(() => {
        if (selectedElementIds.length === 0) return
        const idSet = new Set(selectedElementIds)
        const copied = []
        for (const layer of selectableLayers) {
            for (const el of (layer.elements || [])) {
                if (idSet.has(el._id)) copied.push(el)
            }
        }
        clipboard.current = JSON.parse(JSON.stringify(copied))
    }, [selectedElementIds, selectableLayers])

    const cutElements = useCallback(() => {
        copyElements()
        deleteSelectedElements()
    }, [copyElements, deleteSelectedElements])

    const pasteElements = useCallback(() => {
        if (!activeLayerId || clipboard.current.length === 0) return
        if (activeLayer?.locked) return
        pushUndo()
        const newElements = clipboard.current.map(el => ({
            ...JSON.parse(JSON.stringify(el)),
            _id: nanoid(),
        }))
        setLayers(prev => prev.map(l =>
            l._id === activeLayerId
                ? { ...l, elements: [...l.elements, ...newElements] }
                : l
        ))
        setSelectedElementIds(newElements.map(el => el._id))
        setGroupRotation(0)
    }, [activeLayerId, activeLayer, pushUndo])

    const duplicateElements = useCallback(() => {
        if (selectedElementIds.length === 0 || !activeLayerId) return
        if (activeLayer?.locked) return
        const idSet = new Set(selectedElementIds)
        const toDuplicate = []
        for (const layer of selectableLayers) {
            for (const el of (layer.elements || [])) {
                if (idSet.has(el._id)) toDuplicate.push(el)
            }
        }
        if (toDuplicate.length === 0) return
        pushUndo()
        const OFFSET = 30
        const newElements = toDuplicate.map(el => {
            const copy = { ...JSON.parse(JSON.stringify(el)), _id: nanoid() }
            if (copy.kind === 'image') {
                copy.x = (copy.x ?? 0) + OFFSET
                copy.y = (copy.y ?? 0) + OFFSET
            } else if (copy.kind === 'path' && copy.points) {
                copy.points = copy.points.map(p => ({ ...p, x: p.x + OFFSET, y: p.y + OFFSET }))
            }
            return copy
        })
        const newIdSet = new Set(newElements.map(el => el._id))
        const copyMap = new Map(toDuplicate.map((orig, i) => [orig._id, newElements[i]]))
        setLayers(prev => prev.map(l => {
            if (l._id !== activeLayerId) return l
            const result = []
            for (const el of l.elements) {
                result.push(el)
                if (idSet.has(el._id) && copyMap.has(el._id)) {
                    result.push(copyMap.get(el._id))
                }
            }
            return { ...l, elements: result }
        }))
        setSelectedElementIds(newElements.map(el => el._id))
        setGroupRotation(0)
    }, [selectedElementIds, activeLayerId, activeLayer, selectableLayers, pushUndo])

    useEffect(() => {
        const handler = (e) => {
            const tag = e.target.tagName
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

            if (e.key === 'Escape') {
                if (cropTargetId) {
                    applyCropRef.current?.()
                    return
                }
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault()
                undo()
                return
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
                e.preventDefault()
                redo()
                return
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
                e.preventDefault()
                copyElements()
                return
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'x') {
                e.preventDefault()
                cutElements()
                return
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
                e.preventDefault()
                pasteElements()
                return
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
                e.preventDefault()
                duplicateElements()
                return
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault()
                deleteSelectedElements()
                return
            }
            if (e.key === 'v' || e.key === 'V') {
                if (e.metaKey || e.ctrlKey) return
                setMode('transform')
                return
            }
            if (e.key === 'd' || e.key === 'D') {
                if (e.metaKey || e.ctrlKey) return
                setMode('draw')
                return
            }
            if (e.key === '[') {
                setBrushSize(prev => {
                    const idx = BRUSH_SIZES.indexOf(prev)
                    return idx > 0 ? BRUSH_SIZES[idx - 1] : BRUSH_SIZES[0]
                })
                return
            }
            if (e.key === ']') {
                setBrushSize(prev => {
                    const idx = BRUSH_SIZES.indexOf(prev)
                    return idx < BRUSH_SIZES.length - 1 ? BRUSH_SIZES[idx + 1] : BRUSH_SIZES[BRUSH_SIZES.length - 1]
                })
                return
            }
            if ((e.metaKey || e.ctrlKey) && e.key === '0') {
                e.preventDefault()
                resetZoom()
                return
            }
            if (e.key === '+' || e.key === '=') {
                if (e.metaKey || e.ctrlKey) return
                e.preventDefault()
                zoomToCenter(1.2)
                return
            }
            if (e.key === '-') {
                if (e.metaKey || e.ctrlKey) return
                e.preventDefault()
                zoomToCenter(1 / 1.2)
                return
            }
            if (e.key === 'f' || e.key === 'F') {
                if (e.metaKey || e.ctrlKey) return
                e.preventDefault()
                resetZoom()
                return
            }
            if (e.key === 'o' || e.key === 'O') {
                if (e.metaKey || e.ctrlKey) return
                if (clampedOnionImages.length > 0) {
                    setOnionEnabled(prev => !prev)
                }
                return
            }
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [deleteSelectedElements, undo, redo, copyElements, cutElements, pasteElements, duplicateElements, resetZoom, zoomToCenter, cropTargetId])

    useEffect(() => {
        const viewport = viewportRef.current
        if (!viewport) return
        const handleWheel = (e) => {
            e.preventDefault()
            const sizing = sizingRef.current
            if (!sizing) return
            if (e.ctrlKey || e.metaKey) {
                const sRect = sizing.getBoundingClientRect()
                const focalX = e.clientX - sRect.left
                const focalY = e.clientY - sRect.top
                const delta = -e.deltaY * 0.005
                setZoom(prev => {
                    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev * (1 + delta)))
                    const ratio = next / prev
                    setPanX(px => focalX - ratio * (focalX - px))
                    setPanY(py => focalY - ratio * (focalY - py))
                    return next
                })
            } else {
                setPanX(prev => prev - e.deltaX)
                setPanY(prev => prev - e.deltaY)
            }
        }
        viewport.addEventListener('wheel', handleWheel, { passive: false })
        return () => viewport.removeEventListener('wheel', handleWheel)
    }, [])

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.code === 'Space' && !e.repeat) {
                const tag = e.target.tagName
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
                e.preventDefault()
                spaceHeldRef.current = true
            }
        }
        const handleKeyUp = (e) => {
            if (e.code === 'Space') {
                spaceHeldRef.current = false
                setIsPanning(false)
                panStartRef.current = null
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('keyup', handleKeyUp)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('keyup', handleKeyUp)
        }
    }, [])

    const handleViewportPointerDown = useCallback((e) => {
        if (spaceHeldRef.current || e.button === 1) {
            e.preventDefault()
            setIsPanning(true)
            panStartRef.current = { x: e.clientX - panX, y: e.clientY - panY }
        }
    }, [panX, panY])

    const handleViewportPointerMove = useCallback((e) => {
        if (isPanning && panStartRef.current) {
            setPanX(e.clientX - panStartRef.current.x)
            setPanY(e.clientY - panStartRef.current.y)
        }
    }, [isPanning])

    const handleViewportPointerUp = useCallback(() => {
        if (isPanning) {
            setIsPanning(false)
            panStartRef.current = null
        }
    }, [isPanning])

    // --- Coordinate helper ---

    const canvasPos = useCallback((e, el) => {
        const wrapper = canvasWrapperRef.current
        if (!wrapper) {
            const rect = el.getBoundingClientRect()
            const clientX = e.touches?.[0]?.clientX ?? e.clientX
            const clientY = e.touches?.[0]?.clientY ?? e.clientY
            return {
                x: (clientX - rect.left) / rect.width * width,
                y: (clientY - rect.top) / rect.height * height,
            }
        }
        const rect = wrapper.getBoundingClientRect()
        const clientX = e.touches?.[0]?.clientX ?? e.clientX
        const clientY = e.touches?.[0]?.clientY ?? e.clientY
        return {
            x: (clientX - rect.left) / rect.width * width,
            y: (clientY - rect.top) / rect.height * height,
        }
    }, [width, height])

    function getVisibleBounds(el) {
        if (el.kind !== 'image') return getElementBounds(el, width, height)
        const local = hasCrop(el) ? getCropRect(el, width, height) : getElementLocalBounds(el, width, height)
        if (!local) return null
        const rot = el.rotation ?? 0
        if (rot === 0) return local
        const fullLocal = getElementLocalBounds(el, width, height)
        const center = fullLocal ? getBoundsCenter(fullLocal) : getBoundsCenter(local)
        const corners = [
            { x: local.minX, y: local.minY }, { x: local.maxX, y: local.minY },
            { x: local.maxX, y: local.maxY }, { x: local.minX, y: local.maxY },
        ]
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (const c of corners) {
            const r = rotatePoint(c.x, c.y, center.x, center.y, rot)
            if (r.x < minX) minX = r.x
            if (r.y < minY) minY = r.y
            if (r.x > maxX) maxX = r.x
            if (r.y > maxY) maxY = r.y
        }
        return { minX, minY, maxX, maxY }
    }

    function computeSnapDelta(elements, snapshots, dx, dy) {
        const onlyImages = elements.every(el => el.kind === 'image')
        if (!onlyImages) return { dx, dy, lines: null }

        const snapMap = new Map(snapshots.map(s => [s._id, s]))
        let groupMinX = Infinity, groupMinY = Infinity, groupMaxX = -Infinity, groupMaxY = -Infinity
        let hasBounds = false

        for (const el of elements) {
            const snap = snapMap.get(el._id)
            if (!snap) continue
            const moved = { ...el, x: snap.x + dx, y: snap.y + dy }
            const vb = getVisibleBounds(moved)
            if (!vb) continue
            hasBounds = true
            if (vb.minX < groupMinX) groupMinX = vb.minX
            if (vb.minY < groupMinY) groupMinY = vb.minY
            if (vb.maxX > groupMaxX) groupMaxX = vb.maxX
            if (vb.maxY > groupMaxY) groupMaxY = vb.maxY
        }

        if (!hasBounds) return { dx, dy, lines: null }

        const cx = (groupMinX + groupMaxX) / 2
        const cy = (groupMinY + groupMaxY) / 2
        const canvasCx = width / 2
        const canvasCy = height / 2

        let snapDx = 0, snapDy = 0
        const lines = []

        const edgesX = [
            { val: groupMinX, canvas: 0 },
            { val: groupMaxX, canvas: width },
            { val: cx, canvas: canvasCx },
        ]
        const edgesY = [
            { val: groupMinY, canvas: 0 },
            { val: groupMaxY, canvas: height },
            { val: cy, canvas: canvasCy },
        ]

        let bestSnapX = null, bestDistX = SNAP_THRESHOLD + 1
        for (const e of edgesX) {
            const dist = Math.abs(e.val - e.canvas)
            if (dist < bestDistX) {
                bestDistX = dist
                bestSnapX = e
            }
        }
        let bestSnapY = null, bestDistY = SNAP_THRESHOLD + 1
        for (const e of edgesY) {
            const dist = Math.abs(e.val - e.canvas)
            if (dist < bestDistY) {
                bestDistY = dist
                bestSnapY = e
            }
        }

        if (bestSnapX) {
            snapDx = bestSnapX.canvas - bestSnapX.val
            lines.push({ axis: 'x', pos: bestSnapX.canvas })
        }
        if (bestSnapY) {
            snapDy = bestSnapY.canvas - bestSnapY.val
            lines.push({ axis: 'y', pos: bestSnapY.canvas })
        }

        return { dx: dx + snapDx, dy: dy + snapDy, lines: lines.length > 0 ? lines : null }
    }

    // --- Hit testing ---

    function hitTestElement(el, pos) {
        const local = getElementLocalBounds(el, width, height)
        if (!local) return false
        const rot = el.rotation ?? 0
        let testPos = pos
        if (rot !== 0) {
            const center = getBoundsCenter(local)
            testPos = rotatePoint(pos.x, pos.y, center.x, center.y, -rot)
        }
        const hitRect = (el.kind === 'image' && hasCrop(el)) ? getCropRect(el, width, height) : local
        return testPos.x >= hitRect.minX && testPos.x <= hitRect.maxX && testPos.y >= hitRect.minY && testPos.y <= hitRect.maxY
    }

    function hitTestElements(elements, pos) {
        for (let i = (elements || []).length - 1; i >= 0; i--) {
            if (hitTestElement(elements[i], pos)) return elements[i]
        }
        return null
    }

    function hitTestAcrossLayers(pos) {
        const layersTopFirst = [...selectableLayers].reverse()
        for (const layer of layersTopFirst) {
            const hit = hitTestElements(layer.elements, pos)
            if (hit) return { element: hit, layerId: layer._id }
        }
        return null
    }

    function rotatePoint(px, py, cx, cy, angleDeg) {
        const rad = (angleDeg * Math.PI) / 180
        const cos = Math.cos(rad)
        const sin = Math.sin(rad)
        const dx = px - cx
        const dy = py - cy
        return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos }
    }

    function hitTestGroupHandles(bounds, center, rotation, pos) {
        if (!bounds) return null
        const handles = getHandlePositions(bounds)
        const pad = HANDLE_SIZE / 2 + 6
        for (const h of handles) {
            const rh = rotation !== 0 ? rotatePoint(h.x, h.y, center.x, center.y, rotation) : h
            if (Math.abs(pos.x - rh.x) <= pad && Math.abs(pos.y - rh.y) <= pad) return h
        }
        return null
    }

    function hitTestGroupRotationHandle(bounds, center, rotation, pos) {
        if (!bounds) return false
        const rh = getRotationHandlePos(bounds)
        if (!rh) return false
        const rotated = rotation !== 0 ? rotatePoint(rh.x, rh.y, center.x, center.y, rotation) : rh
        const pad = ROTATION_HANDLE_RADIUS + 8
        return Math.abs(pos.x - rotated.x) <= pad && Math.abs(pos.y - rotated.y) <= pad
    }

    function hitTestGroupBody(bounds, center, rotation, pos) {
        if (!bounds) return false
        let testPos = pos
        if (rotation !== 0) {
            testPos = rotatePoint(pos.x, pos.y, center.x, center.y, -rotation)
        }
        return testPos.x >= bounds.minX && testPos.x <= bounds.maxX && testPos.y >= bounds.minY && testPos.y <= bounds.maxY
    }

    // --- Snapshot helpers for multi-element transforms ---

    function snapshotSelectedElementsAcrossLayers(ids) {
        const idSet = new Set(ids)
        const result = []
        for (const layer of selectableLayers) {
            for (const el of (layer.elements || [])) {
                if (!idSet.has(el._id)) continue
                if (el.kind === 'path') {
                    result.push({ _id: el._id, kind: 'path', points: el.points.map(p => ({ x: p.x, y: p.y, w: p.w })) })
                } else {
                    result.push({
                        _id: el._id, kind: 'image',
                        x: el.x ?? 0, y: el.y ?? 0,
                        scaleX: el.scaleX ?? 1, scaleY: el.scaleY ?? 1,
                        rotation: el.rotation ?? 0,
                        cropTop: el.cropTop ?? 0, cropRight: el.cropRight ?? 0,
                        cropBottom: el.cropBottom ?? 0, cropLeft: el.cropLeft ?? 0,
                    })
                }
            }
        }
        return result
    }

    // --- Crop helpers (images only) ---

    const findElementById = useCallback((id) => {
        for (const layer of selectableLayers) {
            for (const el of (layer.elements || [])) {
                if (el._id === id) return el
            }
        }
        return null
    }, [selectableLayers])

    const enterCropMode = useCallback((el) => {
        if (el.kind !== 'image') return
        const local = getElementLocalBounds(el, width, height)
        if (!local) return
        const rect = getCropRect(el, width, height)
        setCropTargetId(el._id)
        setCropEditRect(rect)
        setSelectedElementIds([el._id])
        setGroupRotation(0)
        setFrozenBounds(null)
        setFrozenCenter(null)
    }, [width, height])

    const applyCrop = useCallback(() => {
        if (!cropTargetId || !cropEditRect) {
            setCropTargetId(null)
            setCropEditRect(null)
            return
        }
        const el = findElementById(cropTargetId)
        if (!el) {
            setCropTargetId(null)
            setCropEditRect(null)
            return
        }
        const local = getElementLocalBounds(el, width, height)
        if (!local) {
            setCropTargetId(null)
            setCropEditRect(null)
            return
        }
        const ct = Math.max(0, cropEditRect.minY - local.minY)
        const cl = Math.max(0, cropEditRect.minX - local.minX)
        const cb = Math.max(0, local.maxY - cropEditRect.maxY)
        const cr = Math.max(0, local.maxX - cropEditRect.maxX)
        const hasCropValues = ct > 0.5 || cl > 0.5 || cb > 0.5 || cr > 0.5

        pushUndo()
        setLayers(prev => prev.map(l => ({
            ...l,
            elements: (l.elements || []).map(e =>
                e._id === cropTargetId
                    ? {
                        ...e,
                        cropTop: hasCropValues ? ct : 0,
                        cropRight: hasCropValues ? cr : 0,
                        cropBottom: hasCropValues ? cb : 0,
                        cropLeft: hasCropValues ? cl : 0,
                    }
                    : e
            ),
        })))
        setCropTargetId(null)
        setCropEditRect(null)
    }, [cropTargetId, cropEditRect, findElementById, width, height, pushUndo])
    applyCropRef.current = applyCrop

    const hitTestCropHandles = useCallback((cropRect, pos) => {
        if (!cropRect) return null
        const handles = getCropHandlePositions(cropRect)
        const pad = CROP_HANDLE_SIZE / 2 + 6
        for (const h of handles) {
            if (Math.abs(pos.x - h.x) <= pad && Math.abs(pos.y - h.y) <= pad) return h
        }
        return null
    }, [])

    const hitTestCropBody = useCallback((cropRect, pos) => {
        if (!cropRect) return false
        return pos.x >= cropRect.minX && pos.x <= cropRect.maxX &&
            pos.y >= cropRect.minY && pos.y <= cropRect.maxY
    }, [])

    // --- Drawing / Transform handlers ---

    const handleDown = useCallback((e) => {
        if (spaceHeldRef.current || isPanning) return
        e.preventDefault()
        e.stopPropagation()

        if (mode === 'transform') {
            const pos = canvasPos(e, e.currentTarget)
            const isMultiKey = e.metaKey || e.ctrlKey

            const now = performance.now()
            const last = lastClickRef.current
            const isDoubleClick = (now - last.time < 400) &&
                Math.abs(pos.x - last.x) < 10 && Math.abs(pos.y - last.y) < 10
            lastClickRef.current = { time: now, x: pos.x, y: pos.y }

            if (cropTargetId && cropEditRect) {
                const el = findElementById(cropTargetId)
                const elRot = el ? (el.rotation ?? 0) : 0
                const elLocal = el ? getElementLocalBounds(el, width, height) : null
                const elCenter = elLocal ? getBoundsCenter(elLocal) : null
                const localPos = (elRot !== 0 && elCenter)
                    ? rotatePoint(pos.x, pos.y, elCenter.x, elCenter.y, -elRot)
                    : pos

                const cropHandle = hitTestCropHandles(cropEditRect, localPos)
                if (cropHandle) {
                    setTransformAction({
                        type: 'crop-handle',
                        handleId: cropHandle.id,
                        startPos: pos,
                        startCropRect: { ...cropEditRect },
                        elementBounds: elLocal,
                        elementRotation: elRot,
                        elementCenter: elCenter,
                    })
                    return
                }

                if (hitTestCropBody(cropEditRect, localPos)) {
                    return
                }

                applyCrop()
                return
            }

            if (isDoubleClick && !isMultiKey) {
                const crossHit = hitTestAcrossLayers(pos)
                if (crossHit && crossHit.element.kind === 'image') {
                    enterCropMode(crossHit.element)
                    lastClickRef.current = { time: 0, x: 0, y: 0 }
                    return
                }
            }

            if (selectedElementIds.length > 0 && selectionBounds && selectionCenter) {
                const hitPivot = selectionPivot ?? selectionCenter
                if (hitTestGroupRotationHandle(selectionBounds, hitPivot, liveRotation, pos)) {
                    pushUndo()
                    const snap = snapshotSelectedElementsAcrossLayers(selectedElementIds)
                    const isSingleImage = selectedElementIds.length === 1
                        && selectedElements.length === 1 && selectedElements[0].kind === 'image'
                    if (!isSingleImage) {
                        setGroupRotation(liveRotation)
                        setFrozenBounds({ ...selectionBounds })
                        setFrozenCenter({ ...selectionCenter })
                    }
                    setTransformAction({
                        type: 'rotate-group',
                        elementIds: [...selectedElementIds],
                        center: { ...selectionCenter },
                        startRotation: liveRotation,
                        snapshots: snap,
                    })
                    return
                }

                const handle = hitTestGroupHandles(selectionBounds, hitPivot, liveRotation, pos)
                if (handle) {
                    pushUndo()
                    const halfW = (selectionBounds.maxX - selectionBounds.minX) / 2
                    const halfH = (selectionBounds.maxY - selectionBounds.minY) / 2
                    const snap = snapshotSelectedElementsAcrossLayers(selectedElementIds)
                    setTransformAction({
                        type: 'scale-group',
                        elementIds: [...selectedElementIds],
                        center: { ...selectionCenter },
                        halfW,
                        halfH,
                        elRotation: liveRotation,
                        snapshots: snap,
                    })
                    return
                }

                if (!isMultiKey && hitTestGroupBody(selectionBounds, hitPivot, liveRotation, pos)) {
                    const crossHit = hitTestAcrossLayers(pos)
                    if (crossHit && !selectedElementIds.includes(crossHit.element._id)) {
                        const newIds = [crossHit.element._id]
                        setSelectedElementIds(newIds)
                        setActiveLayerId(crossHit.layerId)
                        setGroupRotation(0)
                        pushUndo()
                        const snap = snapshotSelectedElementsAcrossLayers(newIds)
                        setTransformAction({
                            type: 'move-group',
                            elementIds: [...newIds],
                            startPos: pos,
                            snapshots: snap,
                        })
                        return
                    }
                    pushUndo()
                    const snap = snapshotSelectedElementsAcrossLayers(selectedElementIds)
                    setTransformAction({
                        type: 'move-group',
                        elementIds: [...selectedElementIds],
                        startPos: pos,
                        snapshots: snap,
                    })
                    return
                }
            }

            const crossHit = hitTestAcrossLayers(pos)
            if (crossHit) {
                const hitEl = crossHit.element
                let newIds
                if (isMultiKey) {
                    const exists = selectedElementIds.includes(hitEl._id)
                    newIds = exists
                        ? selectedElementIds.filter(id => id !== hitEl._id)
                        : [...selectedElementIds, hitEl._id]
                } else {
                    newIds = [hitEl._id]
                }
                setSelectedElementIds(newIds)
                setActiveLayerId(crossHit.layerId)
                setGroupRotation(0)

                if (newIds.length > 0 && newIds.includes(hitEl._id)) {
                    pushUndo()
                    const snap = snapshotSelectedElementsAcrossLayers(newIds)
                    setTransformAction({
                        type: 'move-group',
                        elementIds: [...newIds],
                        startPos: pos,
                        snapshots: snap,
                    })
                }
                return
            }

            if (!isMultiKey) {
                setSelectedElementIds([])
                setGroupRotation(0)
                setFrozenBounds(null)
                setFrozenCenter(null)
            }
            setMarquee({ startX: pos.x, startY: pos.y, x: pos.x, y: pos.y, additive: isMultiKey })
            return
        }

        if (mode !== 'draw') return
        if (!activeLayer?.visible || activeLayer?.locked) return

        const pos = canvasPos(e, e.currentTarget)
        setActiveShape({
            _id: nanoid(),
            kind: 'path',
            points: [{ ...pos, w: brushSize }],
            color: brushColor,
            size: brushSize,
            layerId: activeLayerId,
            _lastTime: performance.now(),
        })
    }, [activeLayer, mode, canvasPos, width, height, brushColor, brushSize, activeLayerId, selectedElementIds, selectedElements, selectionBounds, selectionCenter, selectionPivot, liveRotation, pushUndo, isPanning, selectableLayers, cropTargetId, cropEditRect, hitTestCropHandles, hitTestCropBody, applyCrop, enterCropMode, findElementById])

    const handleMove = useCallback((e) => {
        updateCursorPos(e)
        if (isPanning) return

        if (mode === 'transform' && transformAction) {
            e.preventDefault()
            e.stopPropagation()
            const pos = canvasPos(e, e.currentTarget)

            if (transformAction.type === 'crop-handle') {
                const { handleId, startPos, startCropRect, elementBounds, elementRotation, elementCenter } = transformAction
                let dx = pos.x - startPos.x
                let dy = pos.y - startPos.y
                if (elementRotation && elementCenter) {
                    const rad = (-elementRotation * Math.PI) / 180
                    const cos = Math.cos(rad), sin = Math.sin(rad)
                    const rdx = dx * cos - dy * sin
                    const rdy = dx * sin + dy * cos
                    dx = rdx
                    dy = rdy
                }
                const eb = elementBounds

                setCropEditRect(() => {
                    let { minX, minY, maxX, maxY } = startCropRect
                    const edge = handleId.replace('crop-', '')

                    if (edge.includes('l')) minX = startCropRect.minX + dx
                    if (edge.includes('r')) maxX = startCropRect.maxX + dx
                    if (edge.includes('t')) minY = startCropRect.minY + dy
                    if (edge.includes('b')) maxY = startCropRect.maxY + dy

                    if (eb) {
                        minX = Math.max(eb.minX, Math.min(minX, maxX - 1))
                        minY = Math.max(eb.minY, Math.min(minY, maxY - 1))
                        maxX = Math.min(eb.maxX, Math.max(maxX, minX + 1))
                        maxY = Math.min(eb.maxY, Math.max(maxY, minY + 1))
                    }

                    return { minX, minY, maxX, maxY }
                })
                return
            }

            if (transformAction.type === 'move-group') {
                let rawDx = pos.x - transformAction.startPos.x
                let rawDy = pos.y - transformAction.startPos.y
                const idSet = new Set(transformAction.elementIds)
                const snapshots = transformAction.snapshots
                const snapMap = new Map(snapshots.map(s => [s._id, s]))

                const movedEls = snapshots.map(s => ({ ...s, x: (s.x ?? 0) + rawDx, y: (s.y ?? 0) + rawDy }))
                const { dx, dy, lines } = computeSnapDelta(movedEls, snapshots, rawDx, rawDy)
                setSnapLines(lines)

                setLayers(prev => prev.map(l => {
                    const hasAny = (l.elements || []).some(el => idSet.has(el._id))
                    if (!hasAny) return l
                    return {
                        ...l,
                        elements: l.elements.map(el => {
                            if (!idSet.has(el._id)) return el
                            const snap = snapMap.get(el._id)
                            if (!snap) return el
                            if (el.kind === 'path') {
                                return { ...el, points: snap.points.map(p => ({ x: p.x + dx, y: p.y + dy, w: p.w })) }
                            }
                            if (el.kind === 'image') {
                                return { ...el, x: snap.x + dx, y: snap.y + dy }
                            }
                            return el
                        }),
                    }
                }))
                return
            }

            if (transformAction.type === 'scale-group') {
                const { center, halfW, halfH, elRotation, snapshots } = transformAction
                if (halfW === 0 || halfH === 0) return

                const dx = pos.x - center.x
                const dy = pos.y - center.y
                let localDx, localDy
                if (elRotation) {
                    const rad = (-elRotation * Math.PI) / 180
                    localDx = dx * Math.cos(rad) - dy * Math.sin(rad)
                    localDy = dx * Math.sin(rad) + dy * Math.cos(rad)
                } else {
                    localDx = dx
                    localDy = dy
                }

                let sx = Math.abs(localDx) / halfW
                let sy = Math.abs(localDy) / halfH

                if (e.shiftKey) {
                    const uniform = Math.max(sx, sy)
                    sx = uniform
                    sy = uniform
                }

                const idSet = new Set(transformAction.elementIds)
                const snapMap = new Map(snapshots.map(s => [s._id, s]))

                setLayers(prev => prev.map(l => {
                    const hasAny = (l.elements || []).some(el => idSet.has(el._id))
                    if (!hasAny) return l
                    return {
                        ...l,
                        elements: l.elements.map(el => {
                            if (!idSet.has(el._id)) return el
                            const snap = snapMap.get(el._id)
                            if (!snap) return el
                            if (el.kind === 'path') {
                                return {
                                    ...el,
                                    points: snap.points.map(p => ({
                                        x: center.x + (p.x - center.x) * sx,
                                        y: center.y + (p.y - center.y) * sy,
                                        w: p.w,
                                    })),
                                }
                            }
                            if (el.kind === 'image') {
                                const newScaleX = snap.scaleX * sx
                                const newScaleY = snap.scaleY * sy
                                const newX = center.x + (snap.x - center.x) * sx
                                const newY = center.y + (snap.y - center.y) * sy
                                return {
                                    ...el,
                                    x: newX, y: newY,
                                    scaleX: newScaleX, scaleY: newScaleY,
                                    cropTop: snap.cropTop * sy,
                                    cropRight: snap.cropRight * sx,
                                    cropBottom: snap.cropBottom * sy,
                                    cropLeft: snap.cropLeft * sx,
                                }
                            }
                            return el
                        }),
                    }
                }))
                return
            }

            if (transformAction.type === 'rotate-group') {
                const { center, startRotation } = transformAction
                const angle = Math.atan2(pos.y - center.y, pos.x - center.x) * (180 / Math.PI)
                const startAngle = transformAction._startAngle ?? angle
                if (transformAction._startAngle == null) {
                    setTransformAction(prev => ({ ...prev, _startAngle: angle }))
                    return
                }
                let newRotation = startRotation + (angle - startAngle)
                if (e.shiftKey) {
                    newRotation = Math.round(newRotation / 15) * 15
                }
                setGroupRotation(newRotation)

                const deltaRot = newRotation - startRotation
                const idSet = new Set(transformAction.elementIds)
                const snapMap = new Map(transformAction.snapshots.map(s => [s._id, s]))

                setLayers(prev => prev.map(l => {
                    const hasAny = (l.elements || []).some(el => idSet.has(el._id))
                    if (!hasAny) return l
                    return {
                        ...l,
                        elements: l.elements.map(el => {
                            if (!idSet.has(el._id)) return el
                            const snap = snapMap.get(el._id)
                            if (!snap) return el
                            if (el.kind === 'path') {
                                return {
                                    ...el,
                                    points: snap.points.map(p => ({ ...rotatePoint(p.x, p.y, center.x, center.y, deltaRot), w: p.w })),
                                }
                            }
                            if (el.kind === 'image') {
                                const imgW = width * snap.scaleX
                                const imgH = height * snap.scaleY
                                const newRot = (snap.rotation ?? 0) + deltaRot

                                const snapImgCx = snap.x + imgW / 2
                                const snapImgCy = snap.y + imgH / 2
                                const newImgCenter = rotatePoint(snapImgCx, snapImgCy, center.x, center.y, deltaRot)
                                return {
                                    ...el,
                                    x: newImgCenter.x - imgW / 2,
                                    y: newImgCenter.y - imgH / 2,
                                    rotation: newRot,
                                }
                            }
                            return el
                        }),
                    }
                }))
                return
            }

            return
        }

        if (marquee) {
            e.preventDefault()
            e.stopPropagation()
            const pos = canvasPos(e, e.currentTarget)
            setMarquee(prev => ({ ...prev, x: pos.x, y: pos.y }))
            return
        }

        if (!activeShape) return
        e.preventDefault()
        e.stopPropagation()

        const pos = canvasPos(e, e.currentTarget)
        const now = performance.now()
        setActiveShape(prev => {
            if (!prev || !prev.points || prev.points.length === 0) return prev
            const lastPt = prev.points[prev.points.length - 1]
            const dt = Math.max(now - (prev._lastTime || now), 1)
            const dist = Math.hypot(pos.x - lastPt.x, pos.y - lastPt.y)
            const velocity = dist / dt
            const minW = prev.size * 0.15
            const maxW = prev.size
            const targetW = Math.max(minW, maxW - velocity * maxW * 0.4)
            const smoothed = lastPt.w * 0.6 + targetW * 0.4
            return { ...prev, points: [...prev.points, { ...pos, w: smoothed }], _lastTime: now }
        })
    }, [mode, transformAction, activeShape, canvasPos, width, height, marquee, isPanning, brushSize])

    const handleUp = useCallback(() => {
        if (transformAction) {
            if (transformAction.type === 'crop-handle') {
                setTransformAction(null)
                return
            }
            if (transformAction.type === 'rotate-group') {
                const deltaRot = groupRotation - transformAction.startRotation
                if (deltaRot !== 0) {
                    const idSet = new Set(transformAction.elementIds)
                    setLayers(prev => prev.map(l => {
                        const hasAny = (l.elements || []).some(el => idSet.has(el._id))
                        if (!hasAny) return l
                        return {
                            ...l,
                            elements: l.elements.map(el => {
                                if (!idSet.has(el._id)) return el
                                if (el.kind === 'path') {
                                    const bounds = getElementBounds(el, width, height)
                                    if (!bounds) return el
                                    const elCenter = getBoundsCenter(bounds)
                                    const rot = el.rotation ?? 0
                                    if (rot === 0) return el
                                    const rad = (rot * Math.PI) / 180
                                    const cos = Math.cos(rad)
                                    const sin = Math.sin(rad)
                                    return {
                                        ...el,
                                        rotation: 0,
                                        points: el.points.map(p => ({
                                            x: elCenter.x + (p.x - elCenter.x) * cos - (p.y - elCenter.y) * sin,
                                            y: elCenter.y + (p.x - elCenter.x) * sin + (p.y - elCenter.y) * cos,
                                            w: p.w,
                                        })),
                                    }
                                }
                                return el
                            }),
                        }
                    }))
                }
                setGroupRotation(0)
                setFrozenBounds(null)
                setFrozenCenter(null)
            }
            setSnapLines(null)
            setTransformAction(null)
            return
        }

        if (marquee) {
            const mx1 = Math.min(marquee.startX, marquee.x)
            const my1 = Math.min(marquee.startY, marquee.y)
            const mx2 = Math.max(marquee.startX, marquee.x)
            const my2 = Math.max(marquee.startY, marquee.y)
            const additive = marquee.additive
            setMarquee(null)
            if (Math.abs(mx2 - mx1) < 2 && Math.abs(my2 - my1) < 2) return
            const hitIds = []
            for (const layer of selectableLayers) {
                for (const el of (layer.elements || [])) {
                    const b = getElementBounds(el, width, height)
                    if (!b) continue
                    if (b.maxX >= mx1 && b.minX <= mx2 && b.maxY >= my1 && b.minY <= my2) {
                        hitIds.push(el._id)
                    }
                }
            }
            if (additive) {
                const hitSet = new Set(hitIds)
                const prevSet = new Set(selectedElementIds)
                const toggled = selectedElementIds.filter(id => !hitSet.has(id))
                for (const id of hitIds) {
                    if (!prevSet.has(id)) toggled.push(id)
                }
                setSelectedElementIds(toggled)
            } else if (hitIds.length > 0) {
                setSelectedElementIds(hitIds)
            }
            setGroupRotation(0)
            return
        }

        if (!activeShape) return

        pushUndo()
        const finishedElement = {
            _id: activeShape._id,
            kind: 'path',
            points: activeShape.points,
            color: activeShape.color,
            size: activeShape.size,
        }

        setLayers(prev => prev.map(l =>
            l._id === activeShape.layerId
                ? { ...l, elements: [...l.elements, finishedElement] }
                : l
        ))
        setActiveShape(null)
    }, [activeShape, transformAction, groupRotation, width, height, marquee, selectableLayers, pushUndo])

    const updateCursorPos = useCallback((e) => {
        const cursor = cursorRef.current
        const wrapper = canvasWrapperRef.current
        if (!cursor || !wrapper) return
        const rect = wrapper.getBoundingClientRect()
        const sx = wrapper.offsetWidth / rect.width
        const sy = wrapper.offsetHeight / rect.height
        const localX = (e.clientX - rect.left) * sx
        const localY = (e.clientY - rect.top) * sy
        const csSize = brushSize * (wrapper.offsetWidth / width)
        cursor.style.left = `${localX}px`
        cursor.style.top = `${localY}px`
        cursor.style.width = `${csSize}px`
        cursor.style.height = `${csSize}px`
    }, [brushSize, width])

    useEffect(() => {
        const cursor = cursorRef.current
        const wrapper = canvasWrapperRef.current
        if (!cursor || !wrapper) return
        const csSize = brushSize * (wrapper.offsetWidth / width)
        cursor.style.width = `${csSize}px`
        cursor.style.height = `${csSize}px`
    }, [brushSize, width])

    // --- Layer management ---

    const addLayer = useCallback(() => {
        pushUndo()
        const activeIdx = activeLayerId ? layers.findIndex(l => l._id === activeLayerId) : -1
        const activeOrder = activeIdx !== -1 ? layers[activeIdx].order : layers.reduce((m, l) => Math.max(m, l.order), 0)
        const newOrder = activeOrder + 1
        const layer = createLayer(`Layer ${layers.length + 1}`, newOrder)
        setLayers(prev => prev.map(l => l.order >= newOrder ? { ...l, order: l.order + 1 } : l).concat(layer))
        setActiveLayerId(layer._id)
    }, [layers, activeLayerId, pushUndo])

    const uploadImage = useCallback(() => {
        if (!activeLayerId || activeLayer?.locked) return
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.multiple = true
        input.onchange = async (evt) => {
            const files = Array.from(evt.target.files || [])
            if (files.length === 0) return
            pushUndo()
            for (const file of files) {
                try {
                    const res = await apiFetch('/api/upload', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ fileName: file.name, fileType: file.type, folder: 'temp' }),
                    })
                    const { signedUrl, key } = await res.json()
                    await fetch(signedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type, 'x-ms-blob-type': 'BlockBlob' } })
                    const url = getFileUrl(key)
                    const size = await loadImageNaturalSize(url)
                    const scale = size ? computeImageScale(size.width, size.height, width, height) : { scaleX: 1, scaleY: 1 }
                    const newEl = {
                        _id: nanoid(),
                        kind: 'image',
                        imageUrl: url,
                        imageKey: key,
                        x: 0,
                        y: 0,
                        scaleX: scale.scaleX,
                        scaleY: scale.scaleY,
                    }
                    setLayers(prev => prev.map(l =>
                        l._id === activeLayerId
                            ? { ...l, elements: [...l.elements, newEl] }
                            : l
                    ))
                } catch (err) {
                    console.error('Failed to upload image:', err)
                }
            }
        }
        input.click()
    }, [activeLayerId, activeLayer, pushUndo])

    const insertImagesFromAssets = useCallback(async (assetsOrAsset) => {
        if (!activeLayerId || activeLayer?.locked) return
        const assets = Array.isArray(assetsOrAsset) ? assetsOrAsset : [assetsOrAsset]
        const filtered = assets.filter(a => a.url)
        if (filtered.length === 0) return
        const newElements = await Promise.all(filtered.map(async (a) => {
            const size = await loadImageNaturalSize(a.url)
            const scale = size ? computeImageScale(size.width, size.height, width, height) : { scaleX: 1, scaleY: 1 }
            return {
                _id: nanoid(),
                kind: 'image',
                imageUrl: a.url,
                x: 0,
                y: 0,
                scaleX: scale.scaleX,
                scaleY: scale.scaleY,
            }
        }))
        pushUndo()
        setLayers(prev => prev.map(l =>
            l._id === activeLayerId
                ? { ...l, elements: [...l.elements, ...newElements] }
                : l
        ))
    }, [activeLayerId, activeLayer, pushUndo, width, height])

    const handleCanvasDrop = useCallback(async (e) => {
        const raw = e.dataTransfer.getData(GENERATION_DRAG_TYPE)
        if (!raw) return
        e.preventDefault()
        let data
        try { data = JSON.parse(raw) } catch { return }
        if (data.type !== 'image' || !data.url) return

        const size = await loadImageNaturalSize(data.url)
        const scale = size ? computeImageScale(size.width, size.height, width, height) : { scaleX: 1, scaleY: 1 }
        pushUndo()
        const maxOrder = layers.reduce((m, l) => Math.max(m, l.order), 0)
        const layer = createLayer(`Generation`, maxOrder + 1)
        layer.elements = [{
            _id: nanoid(),
            kind: 'image',
            imageUrl: data.url,
            x: 0,
            y: 0,
            scaleX: scale.scaleX,
            scaleY: scale.scaleY,
        }]
        setLayers(prev => [...prev, layer])
        setActiveLayerId(layer._id)
    }, [layers, pushUndo, width, height])

    const handleCanvasDragOver = useCallback((e) => {
        if (e.dataTransfer.types.includes(GENERATION_DRAG_TYPE)) {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'copy'
        }
    }, [])

    const removeLayer = useCallback((id) => {
        pushUndo()
        setLayers(prev => {
            const next = prev.filter(l => l._id !== id)
            if (activeLayerId === id && next.length > 0) {
                setActiveLayerId(next[next.length - 1]._id)
            }
            return next
        })
    }, [activeLayerId, pushUndo])

    const toggleVisibility = useCallback((id) => {
        setLayers(prev => prev.map(l => l._id === id ? { ...l, visible: !l.visible } : l))
    }, [])

    const toggleLock = useCallback((id) => {
        setLayers(prev => prev.map(l => l._id === id ? { ...l, locked: !l.locked } : l))
    }, [])

    const opacityUndoPushed = useRef(false)
    const bgUndoPushed = useRef(false)
    const changeOpacity = useCallback((id, opacity) => {
        if (!opacityUndoPushed.current) {
            pushUndo()
            opacityUndoPushed.current = true
        }
        setLayers(prev => prev.map(l => l._id === id ? { ...l, opacity } : l))
    }, [pushUndo])

    const commitOpacity = useCallback(() => {
        opacityUndoPushed.current = false
    }, [])

    const filtersUndoPushed = useRef(false)
    const changeLayerFilter = useCallback((id, partial) => {
        if (!filtersUndoPushed.current) {
            pushUndo()
            filtersUndoPushed.current = true
        }
        setLayers(prev => prev.map(l =>
            l._id === id
                ? { ...l, filters: { ...DEFAULT_LAYER_FILTERS, ...(l.filters || {}), ...partial } }
                : l
        ))
    }, [pushUndo])

    const commitLayerFilters = useCallback(() => {
        filtersUndoPushed.current = false
    }, [])

    const setLayerBlendMode = useCallback((id, blendMode) => {
        pushUndo()
        setLayers(prev => prev.map(l =>
            l._id === id
                ? { ...l, filters: { ...DEFAULT_LAYER_FILTERS, ...(l.filters || {}), blendMode } }
                : l
        ))
    }, [pushUndo])

    /** Panel order: index 0 = top of list = front-most layer (highest `order`). */
    const reorderLayersByPanelOrder = useCallback((orderedIds) => {
        if (!orderedIds?.length) return
        pushUndo()
        const n = orderedIds.length
        const posById = new Map(orderedIds.map((id, i) => [id, i]))
        setLayers(prev => prev.map(l => {
            const pos = posById.get(l._id)
            if (pos === undefined) return l
            return { ...l, order: n - 1 - pos }
        }))
    }, [pushUndo])

    const clearActiveLayer = useCallback(() => {
        if (!activeLayerId || activeLayer?.locked) return
        pushUndo()
        setLayers(prev => prev.map(l =>
            l._id === activeLayerId ? { ...l, elements: [] } : l
        ))
        setSelectedElementIds([])
    }, [activeLayerId, activeLayer, pushUndo])

    // --- Element z-order ---

    const reorderElements = useCallback((action) => {
        if (selectedElementIds.length === 0) return
        pushUndo()
        const idSet = new Set(selectedElementIds)
        setLayers(prev => prev.map(l => {
            const els = [...(l.elements || [])]
            const indices = els.map((el, i) => idSet.has(el._id) ? i : -1).filter(i => i !== -1)
            if (indices.length === 0) return l

            if (action === 'sendToBack') {
                const selected = indices.map(i => els[i])
                const rest = els.filter((_, i) => !indices.includes(i))
                return { ...l, elements: [...selected, ...rest] }
            }
            if (action === 'bringToFront') {
                const selected = indices.map(i => els[i])
                const rest = els.filter((_, i) => !indices.includes(i))
                return { ...l, elements: [...rest, ...selected] }
            }
            if (action === 'moveBackward') {
                const sorted = [...indices].sort((a, b) => a - b)
                for (const i of sorted) {
                    if (i > 0 && !idSet.has(els[i - 1]._id)) {
                        ;[els[i - 1], els[i]] = [els[i], els[i - 1]]
                    }
                }
                return { ...l, elements: els }
            }
            if (action === 'moveForward') {
                const sorted = [...indices].sort((a, b) => b - a)
                for (const i of sorted) {
                    if (i < els.length - 1 && !idSet.has(els[i + 1]._id)) {
                        ;[els[i], els[i + 1]] = [els[i + 1], els[i]]
                    }
                }
                return { ...l, elements: els }
            }
            return l
        }))
    }, [activeLayerId, selectedElementIds, pushUndo])

    // --- Save / Export ---

    const uploadToS3 = async (dataURL, fileName, folder = 'sketches', autoName = true) => {
        const res = await apiFetch('/api/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileName, fileType: 'image/png', folder, autoName }),
        })
        const { signedUrl, key } = await res.json()
        const blob = await (await fetch(dataURL)).blob()
        await fetch(signedUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': 'image/png', 'x-ms-blob-type': 'BlockBlob' } })
        return { key, url: getFileUrl(key) }
    }

    const singleSelectedImage = useMemo(() => {
        if (selectedElements.length === 1 && selectedElements[0].kind === 'image') return selectedElements[0]
        return null
    }, [selectedElements])

    const handleRemoveBg = useCallback(async () => {
        if (!singleSelectedImage || !show?._id) return
        const el = singleSelectedImage
        const groupId = crypto.randomUUID()
        try {
            setRemovingBg(true)
            pushUndo()
            const result = await runTaskAndPoll({
                modelId: '69bbd6866cbae33f09763f52',
                payload: { image_url: el.imageUrl },
                showId: show._id,
                group: groupId,
            })
            const genRes = await apiFetch(`/api/shows/${show._id}/generations?taskId=${result._id}`)
            const generation = await genRes.json()
            const outputUrl = generation?.url
            if (outputUrl) {
                setLayers(prev => prev.map(l => ({
                    ...l,
                    elements: (l.elements || []).map(e =>
                        e._id === el._id ? { ...e, imageUrl: outputUrl } : e
                    ),
                })))
            }
        } catch (err) {
            console.error('Remove BG failed:', err)
        } finally {
            setRemovingBg(false)
        }
    }, [singleSelectedImage, show, pushUndo, runTaskAndPoll])

    const handleCompose = useCallback(async () => {
        if (!show?._id) return
        try {
            setComposing(true)

            const imageUrls = []

            const fullDataURL = await rasterizeComposite(sortedLayers, width, height, bgColor)
            if (fullDataURL) {
                const { url } = await uploadToS3(fullDataURL, `compose-full-${Date.now()}.png`, 'temp')
                imageUrls.push({name: 'Final Composition Reference', url})
            }

            for (const layer of sortedLayers) {
                if (!layer.visible) continue
                if (!(layer.elements || []).length) continue
                const dataURL = await rasterizeComposite([layer], width, height, 'transparent')
                if (!dataURL) continue
                const { url } = await uploadToS3(dataURL, `compose-layer-${Date.now()}.png`, 'temp')
                imageUrls.push({name: layer.name, url})
            }

            

            if (imageUrls.length === 0) return

            const prompt = `Compose the following images into a signle compostion. Make the elements match the environment (the background layer) in terms of lighting, shadows, depth and color temp. Keep the rest of the image and the background layer exactly the same. Do not add new elements. The following images are the elements to be composed. Use them as a reference and but don't compose them directly. some images might be a sketch for positioning and reference.\nHere is the full list of images tp use:\n${imageUrls.map((e,i) => `- ${i+1}. ${e.name}`).join('\n')}`

            const groupId = crypto.randomUUID()
            const result = await runTaskAndPoll({
                modelId: '69a1151e6cbae33f09763f33',
                payload: {
                    image_urls: imageUrls.map(i => i.url),
                    resolution: '2K',
                    aspect_ratio: '16:9',
                    prompt,
                },
                showId: show._id,
                group: groupId,
            })

            const genRes = await apiFetch(`/api/shows/${show._id}/generations?taskId=${result._id}`)
            const generation = await genRes.json()
            const outputUrl = generation?.url
            if (outputUrl) {
                pushUndo()
                const newLayer = createLayer('Composed', (sortedLayers[sortedLayers.length - 1]?.order ?? 0) + 1)
                newLayer.elements = [{
                    _id: nanoid(),
                    kind: 'image',
                    imageUrl: outputUrl,
                    x: 0, y: 0,
                    scaleX: 1, scaleY: 1,
                    rotation: 0,
                }]
                setLayers(prev => [...prev, newLayer])
                setActiveLayerId(newLayer._id)
            }
        } catch (err) {
            console.error('Compose failed:', err)
        } finally {
            setComposing(false)
        }
    }, [show, sortedLayers, width, height, bgColor, pushUndo, runTaskAndPoll])

    const handleSave = useCallback(async () => {
        if (!show) return
        setSaving(true)
        try {
            const layerData = []
            for (const layer of sortedLayers) {
                const serializedElements = []
                for (const el of (layer.elements || [])) {
                    if (el.kind === 'path') {
                        serializedElements.push({
                            kind: 'path',
                            points: el.points,
                            color: el.color,
                            size: el.size,
                            rotation: el.rotation ?? 0,
                        })
                    } else if (el.kind === 'image') {
                        const imgEntry = {
                            kind: 'image',
                            x: el.x ?? 0,
                            y: el.y ?? 0,
                            scaleX: el.scaleX ?? 1,
                            scaleY: el.scaleY ?? 1,
                            rotation: el.rotation ?? 0,
                            cropTop: el.cropTop ?? 0,
                            cropRight: el.cropRight ?? 0,
                            cropBottom: el.cropBottom ?? 0,
                            cropLeft: el.cropLeft ?? 0,
                        }
                        if (el._file) {
                            const dataURL = await fileToDataURL(el._file)
                            const { key, url } = await uploadToS3(dataURL, el._file.name, 'temp')
                            imgEntry.imageUrl = url
                            imgEntry.imageKey = key
                        } else {
                            imgEntry.imageUrl = el.imageUrl
                            imgEntry.imageKey = el.imageKey
                        }
                        serializedElements.push(imgEntry)
                    }
                }

                layerData.push({
                    name: layer.name,
                    visible: layer.visible,
                    locked: layer.locked,
                    opacity: layer.opacity,
                    order: layer.order,
                    filters: { ...DEFAULT_LAYER_FILTERS, ...(layer.filters || {}) },
                    elements: serializedElements,
                })
            }

            const thumbnail = await rasterizeComposite(sortedLayers, width, height, bgColor)
            let imageUrl, imageKey
            if (thumbnail) {
                const result = await uploadToS3(thumbnail, `${dbSketchId}.png`, 'sketches')
                imageUrl = result.url
                imageKey = result.key
            }

            const body = { name: sketchName, width, height, bgColor, layers: layerData, imageUrl, imageKey }

            let res
            if (dbSketchId) {
                res = await apiFetch(`/api/shows/${show._id}/sketches/${dbSketchId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                })
            } else {
                res = await apiFetch(`/api/shows/${show._id}/sketches`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                })
            }

            const saved = await res.json()
            if (saved._id) setDbSketchId(saved._id)
            if (onSaveComplete && imageUrl) {
                onSaveComplete({
                    sketchId: saved._id || dbSketchId,
                    imageUrl,
                    imageKey,
                    name: sketchName,
                })
            }
        } catch (err) {
            console.error('Save failed:', err)
        } finally {
            setSaving(false)
        }
    }, [show, sortedLayers, sketchName, width, height, bgColor, dbSketchId, onSaveComplete])

    const handleExport = useCallback(async () => {
        const dataURL = await rasterizeComposite(sortedLayers, width, height, bgColor)
        if (!dataURL) return
        const link = document.createElement('a')
        link.download = `${sketchName || 'sketch'}.png`
        link.href = dataURL
        link.click()
    }, [sortedLayers, width, height, bgColor, sketchName])

    const capturePreview = useCallback(async () => {
        const dataURL = await rasterizeComposite(sortedLayers, width, height, bgColor)
        if (!dataURL) throw new Error('Failed to rasterize sketch')
        const { url } = await uploadToS3(dataURL, `preview-${Date.now()}.png`, 'temp')
        return url
    }, [sortedLayers, width, height, bgColor])

    const sketchPreview = useMemo(() => {
        return { url: capturePreview, type: 'image' }
    }, [capturePreview])

    const cursorForMode = mode === 'transform' ? 'default' : 'none'

    return (
        <div className="flex flex-col h-full w-full bg-background relative">
            
            <div className='flex items-center justify-between px-4 h-20 border-b'>
                <div className='flex items-center gap-2'>
                    <button onClick={onClose} className='text-secondary-foreground hover:text-white cursor-pointer px-2 flex justify-center items-center rounded-md'>
                        <FaChevronLeft className='w-4 h-4' />
                    </button>
                    {isRenaming ? (
                        <input
                            ref={renameInputRef}
                            className='text-2xl font-bold text-secondary-foreground bg-transparent border-b border-primary outline-none'
                            defaultValue={sketchName}
                            onBlur={(e) => {
                                const val = e.target.value.trim()
                                if (val && val !== sketchName) setSketchName(val)
                                setIsRenaming(false)
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') e.target.blur()
                                if (e.key === 'Escape') setIsRenaming(false)
                            }}
                        />
                    ) : (
                        <div className='flex items-center gap-2'>
                            <h1 className='text-2xl font-bold text-secondary-foreground select-none'>{sketchName}</h1>
                            <button onClick={() => setIsRenaming(true)} className='text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer'>
                                <FiEdit2 className='w-3 h-3' />
                            </button>
                        </div>
                    )}
                </div>
                <div className='flex items-center gap-2'>
                    {onClose && (
                        <Button type="danger-ghost" onClick={onClose}>
                            Cancel
                        </Button>
                    )}
                    <Button type="primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
            <Toolbar
                brushColor={brushColor}
                brushSize={brushSize}
                mode={mode}
                bgColor={bgColor}
                onBgColorInput={(e) => {
                    if (!bgUndoPushed.current) { pushUndo(); bgUndoPushed.current = true }
                    setBgColor(e.target.value)
                }}
                onBgColorChange={(e) => { bgUndoPushed.current = false; setBgColor(e.target.value) }}
                onColorChange={setBrushColor}
                onSizeChange={setBrushSize}
                onModeChange={setMode}
                onClear={clearActiveLayer}
                onExport={handleExport}
                onUploadImage={uploadImage}
                onAddFromAssets={async () => {
                    const result = await openModal(<AssetsBrowserModal mediaType="image" multiSelect />)
                    if (result) insertImagesFromAssets(result)
                }}
                onUndo={undo}
                onRedo={redo}
                canUndo={canUndo}
                canRedo={canRedo}
                onReorderElements={reorderElements}
                hasSelection={selectedElementIds.length > 0}
                onionImageCount={clampedOnionImages.length}
                onionEnabled={onionEnabled}
                onToggleOnion={() => setOnionEnabled(prev => !prev)}
                onionCount={onionCount}
                onOnionCountChange={setOnionCount}
                onionOpacity={onionOpacity}
                onOnionOpacityChange={setOnionOpacity}
                generationsOpen={generationsOpen}
                onToggleGenerations={() => setGenerationsOpen(v => !v)}
                hasImageSelection={!!singleSelectedImage}
                onRemoveBg={handleRemoveBg}
                removingBg={removingBg}
                onCompose={handleCompose}
                composing={composing}
            />

            <div className="flex flex-1 overflow-hidden">
                <div
                    ref={viewportRef}
                    className="flex-1 relative flex items-center justify-center bg-neutral-900 overflow-hidden p-8"
                    style={{ cursor: isPanning || spaceHeldRef.current ? 'grabbing' : undefined }}
                    onPointerDown={handleViewportPointerDown}
                    onPointerMove={handleViewportPointerMove}
                    onPointerUp={handleViewportPointerUp}
                    onPointerLeave={handleViewportPointerUp}
                    onDragOver={handleCanvasDragOver}
                    onDrop={handleCanvasDrop}
                >
                    {/* Floating zoom controls */}
                    <div className="absolute top-3 right-3 z-50 flex items-center gap-1 bg-black/50 rounded-md px-1.5 py-1">
                        <Button type="secondary" size="xs" icon={<FiZoomIn className="w-3 h-3" />} onClick={() => zoomToCenter(1.2)} title="Zoom in (+)" />
                        <button className="text-[10px] font-mono text-secondary-foreground/60 hover:text-secondary-foreground px-1 min-w-8 text-center cursor-pointer" onClick={resetZoom}>
                            {Math.round(zoom * 100)}%
                        </button>
                        <Button type="secondary" size="xs" icon={<FiZoomOut className="w-3 h-3" />} onClick={() => zoomToCenter(1 / 1.2)} title="Zoom out (-)" />
                    </div>

                    <div
                        className={`relative ${fillVertical ? 'h-full' : 'w-full'}`}
                        style={{ aspectRatio: `${width}/${height}` }}
                        ref={el => {
                            sizingRef.current = el
                            if (!el) return
                            if (!el._resizeObserver) {
                                el._resizeObserver = new ResizeObserver(entries => {
                                    for (const entry of entries) {
                                        setFillVertical(entry.contentRect.width / entry.contentRect.height > width / height)
                                    }
                                })
                                el._resizeObserver.observe(el.parentElement)
                            }
                        }}
                    >
                        <div
                            ref={canvasWrapperRef}
                            className="absolute inset-0 origin-top-left"
                            style={{
                                transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
                                willChange: 'transform',
                            }}
                        >
                            <div
                                className="absolute inset-0 shadow-lg"
                                style={{
                                    backgroundColor: bgColor,
                                }}
                            />

                            {onionEnabled && onionCount > 0 && clampedOnionImages.slice(-onionCount).map((src, i) => {
                                const opacity = ((i + 1) / (onionCount + 1)) * onionOpacity
                                return (
                                    <img
                                        key={i}
                                        src={src}
                                        alt=""
                                        draggable={false}
                                        className="absolute inset-0 w-full h-full pointer-events-none select-none"
                                        style={{ opacity, objectFit: 'fill' }}
                                    />
                                )
                            })}

                            {(() => {
                                let cropEl = null
                                if (cropTargetId) {
                                    for (const l of sortedLayers) {
                                        const found = (l.elements || []).find(e => e._id === cropTargetId)
                                        if (found) { cropEl = found; break }
                                    }
                                }
                                const cropRot = cropEl ? (cropEl.rotation ?? 0) : 0
                                const cropLocal = cropEl ? getElementLocalBounds(cropEl, width, height) : null
                                const cropCenter = cropLocal ? getBoundsCenter(cropLocal) : null

                                return [
                                    ...sortedLayers.map((layer) => {
                                        const layerHasCropTarget = cropTargetId && (layer.elements || []).some(e => e._id === cropTargetId)
                                        return (
                                            <VectorLayer
                                                key={layer._id}
                                                className={layerHasCropTarget ? 'z-[15]' : undefined}
                                                layer={layer}
                                                canvasWidth={width}
                                                canvasHeight={height}
                                                activeShape={activeShape?.layerId === layer._id ? activeShape : null}
                                                selectedElementIds={cropTargetId ? [] : selectedElementIds}
                                                cropTargetId={layerHasCropTarget ? cropTargetId : null}
                                                cropEditRect={layerHasCropTarget ? cropEditRect : null}
                                                cropTargetRotation={layerHasCropTarget ? cropRot : 0}
                                                cropTargetCenter={layerHasCropTarget ? cropCenter : null}
                                            />
                                        )
                                    }),
                                    <VectorLayer
                                        key="__overlay"
                                        className="z-[15]"
                                        layer={{ visible: true, opacity: 1, elements: [] }}
                                        canvasWidth={width}
                                        canvasHeight={height}
                                        selectedElementIds={cropTargetId ? [] : selectedElementIds}
                                        selectionBounds={!cropTargetId ? selectionBounds : null}
                                        selectionRotation={!cropTargetId ? liveRotation : 0}
                                        selectionCenter={!cropTargetId ? selectionCenter : null}
                                        selectionPivot={!cropTargetId ? selectionPivot : null}
                                        snapLines={snapLines}
                                    />,
                                ]
                            })()}

                            {/* Tint overspill outside the artboard with the sketch bg color (scales with pan/zoom) */}
                            <div
                                className="absolute inset-0 z-[5] pointer-events-none"
                                style={{ boxShadow: '0 0 0 100000px rgba(23, 23, 23, 0.85)' }}
                                aria-hidden
                            />

                            <div
                                className="absolute z-10"
                                style={{
                                    inset: '-200%',
                                    cursor: isPanning ? 'grabbing' : cursorForMode,
                                }}
                                onMouseDown={handleDown}
                                onMouseMove={handleMove}
                                onMouseUp={handleUp}
                                onMouseLeave={() => { handleUp(); setCursorVisible(false) }}
                                onMouseEnter={() => setCursorVisible(true)}
                                onTouchStart={handleDown}
                                onTouchMove={handleMove}
                                onTouchEnd={handleUp}
                                onPointerDown={(e) => { if (!spaceHeldRef.current && e.button !== 1) e.stopPropagation() }}
                                onClick={(e) => e.stopPropagation()}
                            />

                            {marquee && (
                                <svg
                                    viewBox={`0 0 ${width} ${height}`}
                                    className="absolute inset-0 w-full h-full z-20 pointer-events-none"
                                    preserveAspectRatio="none"
                                >
                                    <rect
                                        x={Math.min(marquee.startX, marquee.x)}
                                        y={Math.min(marquee.startY, marquee.y)}
                                        width={Math.abs(marquee.x - marquee.startX)}
                                        height={Math.abs(marquee.y - marquee.startY)}
                                        fill="rgba(59, 130, 246, 0.1)"
                                        stroke="#3b82f6"
                                        strokeWidth={1.5}
                                        strokeDasharray="6 3"
                                    />
                                </svg>
                            )}

                            {mode !== 'transform' && (
                                <div
                                    ref={cursorRef}
                                    className="pointer-events-none absolute z-20"
                                    style={{
                                        display: cursorVisible ? 'block' : 'none',
                                        width: 0,
                                        height: 0,
                                        border: '1.5px solid rgba(0,0,0,0.5)',
                                        borderRadius: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        boxShadow: '0 0 0 1px rgba(255,255,255,0.3)',
                                    }}
                                />
                            )}

                            {/* Drawing bounds on top of content so overscaled elements don’t hide the canvas edge */}
                            <div
                                className="absolute inset-0 z-[30] box-border border border-white/80 pointer-events-none"
                                aria-hidden
                            />
                        </div>
                    </div>
                </div>

                {generationsOpen ? (
                    <DragPreviewProvider>
                        <StoryboardGenerations preview={sketchPreview} />
                    </DragPreviewProvider>
                ) : (
                    <LayerPanel
                        layers={layers}
                        activeLayerId={activeLayerId}
                        onSelectLayer={setActiveLayerId}
                        onAddLayer={addLayer}
                        onRemoveLayer={removeLayer}
                        onToggleVisibility={toggleVisibility}
                        onToggleLock={toggleLock}
                        onChangeOpacity={changeOpacity}
                        onCommitOpacity={commitOpacity}
                        onChangeLayerFilter={changeLayerFilter}
                        onCommitLayerFilters={commitLayerFilters}
                        onChangeBlendMode={setLayerBlendMode}
                        onReorderLayers={reorderLayersByPanelOrder}
                        onRenameLayer={(id, name) => { pushUndo(); setLayers(prev => prev.map(l => l._id === id ? { ...l, name } : l)) }}
                    />
                )}
            </div>


            {loading && <Loader status="Loading..." />}
        </div>
    )
}

// --- Static helpers ---

function loadImageNaturalSize(url) {
    return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
        img.onerror = () => resolve(null)
        img.crossOrigin = 'anonymous'
        img.src = url
    })
}

function computeImageScale(naturalW, naturalH, canvasW, canvasH) {
    const scaleX = naturalW / canvasW
    const scaleY = naturalH / canvasH
    const maxScale = Math.max(scaleX, scaleY)
    if (maxScale <= 1) return { scaleX, scaleY }
    return { scaleX: scaleX / maxScale, scaleY: scaleY / maxScale }
}

function fileToDataURL(file) {
    return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.readAsDataURL(file)
    })
}

function appendLayerElementsToSvgGroup(svgNs, g, layer, w, h, imagePromises, clipRef) {
    for (const el of layer.elements || []) {
        if (el.kind === 'image') {
            if (!el.imageUrl) continue
            const imgG = document.createElementNS(svgNs, 'g')
            const ix = el.x ?? 0
            const iy = el.y ?? 0
            const isx = el.scaleX ?? 1
            const isy = el.scaleY ?? 1
            const irot = el.rotation ?? 0
            const imgLocal = getElementLocalBounds(el, w, h)
            const imgCenter = imgLocal ? getBoundsCenter(imgLocal) : { x: ix + w * isx / 2, y: iy + h * isy / 2 }
            let imgTransform = `translate(${ix},${iy}) scale(${isx},${isy})`
            if (irot !== 0) {
                imgTransform = `rotate(${irot} ${imgCenter.x} ${imgCenter.y}) ${imgTransform}`
            }
            imgG.setAttribute('transform', imgTransform)

            const imgEl = document.createElementNS(svgNs, 'image')
            imgEl.setAttribute('width', w)
            imgEl.setAttribute('height', h)
            imgEl.setAttribute('preserveAspectRatio', 'none')

            const p = toDataURLFromSrc(el.imageUrl).then(dataUrl => {
                imgEl.setAttributeNS('http://www.w3.org/1999/xlink', 'href', dataUrl)
            })
            imagePromises.push(p)
            imgG.appendChild(imgEl)

            const ct = el.cropTop ?? 0
            const cr = el.cropRight ?? 0
            const cb = el.cropBottom ?? 0
            const cL = el.cropLeft ?? 0
            if (ct !== 0 || cr !== 0 || cb !== 0 || cL !== 0) {
                const local = getElementLocalBounds(el, w, h)
                if (local) {
                    const clipId = `raster-crop-${clipRef.n++}`
                    const defs = document.createElementNS(svgNs, 'defs')
                    const clipPath = document.createElementNS(svgNs, 'clipPath')
                    clipPath.setAttribute('id', clipId)
                    const clipRect = document.createElementNS(svgNs, 'rect')
                    clipRect.setAttribute('x', local.minX + cL)
                    clipRect.setAttribute('y', local.minY + ct)
                    clipRect.setAttribute('width', (local.maxX - cr) - (local.minX + cL))
                    clipRect.setAttribute('height', (local.maxY - cb) - (local.minY + ct))
                    if (irot !== 0) {
                        clipRect.setAttribute('transform', `rotate(${irot} ${imgCenter.x} ${imgCenter.y})`)
                    }
                    clipPath.appendChild(clipRect)
                    defs.appendChild(clipPath)
                    g.appendChild(defs)
                    const wrapper = document.createElementNS(svgNs, 'g')
                    wrapper.setAttribute('clip-path', `url(#${clipId})`)
                    wrapper.appendChild(imgG)
                    g.appendChild(wrapper)
                } else {
                    g.appendChild(imgG)
                }
            } else {
                g.appendChild(imgG)
            }
        } else if (el.kind === 'path') {
            const pts = el.points
            const varWidth = pts.length >= 2 && pts[0].w != null
            const srot = el.rotation ?? 0
            let transform = null
            if (srot !== 0) {
                const sb = getElementBounds(el, w, h)
                if (sb) {
                    const sc = getBoundsCenter(sb)
                    transform = `rotate(${srot} ${sc.x} ${sc.y})`
                }
            }

            if (!varWidth) {
                const path = document.createElementNS(svgNs, 'path')
                path.setAttribute('d', pointsToSvgPath(pts))
                path.setAttribute('stroke', el.color)
                path.setAttribute('stroke-width', el.size)
                path.setAttribute('stroke-linecap', 'round')
                path.setAttribute('stroke-linejoin', 'round')
                path.setAttribute('fill', 'none')
                if (transform) path.setAttribute('transform', transform)
                g.appendChild(path)
            } else {
                const pg = document.createElementNS(svgNs, 'g')
                if (transform) pg.setAttribute('transform', transform)
                for (let i = 1; i < pts.length; i++) {
                    const prev = pts[i - 1]
                    const pt = pts[i]
                    const sw = (prev.w + pt.w) / 2
                    const mx = (prev.x + pt.x) / 2
                    const my = (prev.y + pt.y) / 2
                    const seg = document.createElementNS(svgNs, 'path')
                    seg.setAttribute('d', `M${prev.x},${prev.y}Q${prev.x},${prev.y},${mx},${my}L${pt.x},${pt.y}`)
                    seg.setAttribute('stroke', el.color)
                    seg.setAttribute('stroke-width', sw)
                    seg.setAttribute('stroke-linecap', 'round')
                    seg.setAttribute('stroke-linejoin', 'round')
                    seg.setAttribute('fill', 'none')
                    pg.appendChild(seg)
                }
                g.appendChild(pg)
            }
        }
    }
}

function loadRasterImageFromObjectUrl(url) {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Raster image failed'))
        img.src = url
    })
}

async function rasterizeLayerOnly(layer, w, h) {
    const svgNs = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(svgNs, 'svg')
    svg.setAttribute('xmlns', svgNs)
    svg.setAttribute('width', w)
    svg.setAttribute('height', h)
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`)

    const filterId = 'lf'
    const filterEl = document.createElementNS(svgNs, 'filter')
    filterEl.setAttribute('id', filterId)
    const hasFilter = appendSvgFilterPrimitives(svgNs, filterEl, w, h, layer.filters)
    if (hasFilter) {
        const defs = document.createElementNS(svgNs, 'defs')
        defs.appendChild(filterEl)
        svg.appendChild(defs)
    }

    const g = document.createElementNS(svgNs, 'g')
    g.setAttribute('opacity', String(layer.opacity ?? 1))
    if (hasFilter) g.setAttribute('filter', `url(#${filterId})`)

    const imagePromises = []
    const clipRef = { n: 0 }
    appendLayerElementsToSvgGroup(svgNs, g, layer, w, h, imagePromises, clipRef)
    svg.appendChild(g)

    await Promise.all(imagePromises)
    const svgStr = new XMLSerializer().serializeToString(svg)
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
    const objectUrl = URL.createObjectURL(blob)
    try {
        const img = await loadRasterImageFromObjectUrl(objectUrl)
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, w, h)
        ctx.drawImage(img, 0, 0, w, h)
        return canvas.toDataURL('image/png')
    } catch {
        return null
    } finally {
        URL.revokeObjectURL(objectUrl)
    }
}

async function rasterizeComposite(sortedLayers, w, h, bgColor) {
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (bgColor && bgColor !== 'transparent') {
        ctx.fillStyle = bgColor
        ctx.fillRect(0, 0, w, h)
    }

    const transparentBg = !bgColor || bgColor === 'transparent'
    let firstContentLayer = true

    for (const layer of sortedLayers) {
        if (!layer.visible) continue
        const dataUrl = await rasterizeLayerOnly(layer, w, h)
        if (!dataUrl) continue
        try {
            const img = await loadRasterImageFromObjectUrl(dataUrl)
            ctx.save()
            const lf = normalizeLayerFilters(layer)
            const useBlend = !(firstContentLayer && transparentBg)
            ctx.globalCompositeOperation = useBlend
                ? blendModeToCanvas(lf.blendMode)
                : 'source-over'
            ctx.drawImage(img, 0, 0, w, h)
            ctx.restore()
            firstContentLayer = false
        } catch {
            // skip failed layer
        }
    }

    return canvas.toDataURL('image/png')
}

function toDataURLFromSrc(src) {
    return new Promise((resolve) => {
        if (src.startsWith('data:')) { resolve(src); return }
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
            const c = document.createElement('canvas')
            c.width = img.naturalWidth
            c.height = img.naturalHeight
            c.getContext('2d').drawImage(img, 0, 0)
            resolve(c.toDataURL('image/png'))
        }
        img.onerror = () => resolve(src)
        img.src = src
    })
}
